import { useState, useRef, useEffect } from "react";
import { userLogout } from '../helpers';
import { useIdleTimer } from 'react-idle-timer';
import ClevertapReact from "../utils/clever-tap";
import { useAppInsightsContext, useTrackEvent } from "@microsoft/applicationinsights-react-js";

const useSessionTimeout = (user) => {
  const appInsights = useAppInsightsContext();
  const trackSessionTimeout = useTrackEvent(appInsights, "Session Timeout", {});
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  
  // Session logs out after user is idle for 'timeout' duration (in milliseconds)
  // e.g. timeout = 300000. Session times out after 5 minutes. 
  const timeout = process.env.REACT_APP_SESSION_TIMEOUT;
  // Modal shows 'modalDelay' milliseconds before the session times out
  // e.g. modalDelay= 30000. Modal shows 30 seconds before session times out.
  const modalDelay = process.env.REACT_APP_TIMEOUT_POPUP;
  const [remainingTime, setRemainingTime] = useState(timeout);
  
  // Store references when modal is opened to avoid resetting the timer
  const modalOpenedAtRef = useRef(null);
  const initialRemainingAtModalRef = useRef(null);
    
  const handleStayActive = () => {
    ClevertapReact.event('Session_Timeout_Stay_Logged_In');
    setShowInactivityModal(false);
    reset();
  };
    
  const handleLogout = () => {
    ClevertapReact.event('Session_Timeout_Log_Out');
    const sessionProps = {
      'Event Type': 'Log',
      'Action Taken': 'Session Timed Out',
      'User Identification': user?.user?.userName,
      'Event Source': 'Payroll Portal Application',
      'Details': 'Session Timeout Logout'
    };
    trackSessionTimeout({ sessionProps });
    appInsights.core.flush();
    userLogout();
  };
      
  const { reset, getRemainingTime, pause, isLeader } = useIdleTimer({
    timeout,
    crossTab: true,
    onIdle: () => {
      if (!isLeader()) {
        return;
      }
      handleLogout();
    }
  });
      
  useEffect(() => {
    const interval = setInterval(() => {
      if (!showInactivityModal) {
        const remaining = getRemainingTime();
        setRemainingTime(remaining);
        if (remaining <= modalDelay) {
			    // Pause the timer so any user action (e.g. user hovers over modal) won't reset it
			    // Record the moment when the prompt opens and the remaining time at that moment.
          pause();
          modalOpenedAtRef.current = Date.now();
          initialRemainingAtModalRef.current = remaining;
          setShowInactivityModal(true);
        }
        if (remaining <= 0) {
          handleLogout();
        }
      } else {
        const elapsed = Date.now() - modalOpenedAtRef.current;
        const newRemaining = Math.max(initialRemainingAtModalRef.current - elapsed, 0);
        setRemainingTime(newRemaining);
        if (newRemaining <= 0) {
          handleLogout();
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, [getRemainingTime, showInactivityModal, pause]);

  // Cross-tab handling
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'lastActive') {
        // If lastActive is changed, reset the idle timer and hide the modal.
        reset();
        setShowInactivityModal(false);
        setRemainingTime(timeout);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [reset, timeout]);

  // Cross-tab: Notify activity by updating localStorage whenever the user interacts.
    useEffect(() => {
    const notifyActivity = () => {
      localStorage.setItem('lastActive', Date.now().toString());
    };

    window.addEventListener('mousemove', notifyActivity);
    window.addEventListener('keydown', notifyActivity);
    return () => {
      window.removeEventListener('mousemove', notifyActivity);
      window.removeEventListener('keydown', notifyActivity);
    };
  }, []);

  return {
    handleLogout,
    handleStayActive,
    remainingTime,
    showInactivityModal
  }
}

export default useSessionTimeout;

